(() => {
  'use strict';

  const VERSION = '1.2.0';
  const PORTAL_PATH = /\/portal\/[^/]+\/?(.*)$/;

  if (!PORTAL_PATH.test(location.pathname) || window.__hnSkipOffer) return;
  window.__hnSkipOffer = VERSION;

  // ---------------------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------------------
  const DEFAULTS = {
    offer: { frequency: 2, unit: 'month' },

    switchMode: 'keep_date',

    routes: [/^subscriptions(\/\d+)?$/, /^overview$/, /^$/],

    reloadAfterChange: true,

    actionTimeout: 20000,

    sdkUrl: 'https://static.rechargecdn.com/assets/storefront/recharge-client-1.81.0.min.js',

    testMode: false,

    debug: false,

    copy: {
      title: 'Would {offer} suit you better?',
      body: 'Your last box was delayed too. If yarn is piling up, getting a box {offer} gives you more time to finish each project. You can switch back anytime.',
      switchLabel: 'Switch to {offer}',
      switchDetail: 'Next box {date}, then {offer}',
      delayLabel: 'Delay my next box',
      delayDetail: 'Next box {date}, then {current}',
      switching: 'Switching…',
      delaying: 'Delaying…',
      switchedTitle: 'Your box now comes {offer}',
      switchedBody: 'Your next box is scheduled for {date}.',
      delayedTitle: 'Your next box is delayed',
      delayedBody: "It's now scheduled for {date}.",
      partialBody:
        "It's now scheduled for {date}, but we couldn't switch you to {offer}, so it still comes {current}. You can change how often it comes by editing your subscription.",
      uncertainTitle: 'Please check your subscription',
      uncertainBody: "We couldn't confirm your change. Refresh the page to see your current schedule before trying again.",
      error: "Your subscription wasn't changed. Check your connection and try again.",
      done: 'Done',
      refresh: 'Refresh page',
      close: 'Close',
      frequency: (n, unit) => (n === 1 ? `every ${unit}` : `every ${n} ${unit}s`),
    },
  };

  const overrides = window.HNSkipOfferConfig || {};
  const CONFIG = {
    ...DEFAULTS,
    ...overrides,
    offer: { ...DEFAULTS.offer, ...overrides.offer },
    copy: { ...DEFAULTS.copy, ...overrides.copy },
  };
  const COPY = CONFIG.copy;
  const OFFER_TEXT = COPY.frequency(CONFIG.offer.frequency, CONFIG.offer.unit);
  const ROUTES = [].concat(CONFIG.routes || []).map((r) => (r instanceof RegExp ? r : new RegExp(r)));

  const qaMode = (() => {
    try {
      const fromUrl = new URLSearchParams(location.search).get('hn_skip_offer');
      if (fromUrl) sessionStorage.setItem('hn_skip_offer', fromUrl);
      return sessionStorage.getItem('hn_skip_offer') || '';
    } catch (e) {
      return '';
    }
  })();
  if (qaMode === 'off') return;
  const FORCE = CONFIG.testMode === true || qaMode === 'force';
  const DEBUG = CONFIG.debug || qaMode === 'debug' || FORCE;
  if (FORCE) console.warn('[HN skip offer] TEST MODE: the popup shows on every delay. Turn testMode off before publishing.');

  const log = (...args) => {
    if (DEBUG) console.log('%c[HN skip offer]', 'color:#4d6f61;font-weight:600', ...args);
  };

  // ---------------------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------------------
  const state = {
    rc: null,
    session: null,
    ready: false,
    subs: new Map(), // subscription id (string) → entry, see buildEntry()
    plans: new Map(), // "product:variant" → plan id | null (no such plan)
    open: false,
    root: null,
    bypass: false, // true while handing a click back to Recharge's own flow
    handedOff: false,
    failOpen: false, // after an error, later delays skip the popup
    lastTarget: null,
    lastTargetAt: 0,
    refreshTimer: null,
  };

  // Remember what the customer just interacted with. Used to hand a "Delay" back to
  // Recharge's own flow and to pick up the portal's theme for the popup.
  const deepTarget = (e) => (typeof e.composedPath === 'function' && e.composedPath()[0]) || e.target;
  const deepActive = () => {
    let el = document.activeElement;
    while (el && el.shadowRoot && el.shadowRoot.activeElement) el = el.shadowRoot.activeElement;
    return el;
  };
  const remember = (target) => {
    if (!(target instanceof Element) || (state.root && state.root.contains(target))) return;
    state.lastTarget = target;
    state.lastTargetAt = performance.now();
  };
  document.addEventListener('pointerdown', (e) => remember(deepTarget(e)), true);
  document.addEventListener('click', (e) => remember(deepTarget(e)), true);
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') remember(deepActive());
    },
    true
  );

  // ---------------------------------------------------------------------------------------
  // The intercept
  // ---------------------------------------------------------------------------------------
  document.addEventListener('Recharge::click::skip', (event) => {
    if (state.bypass) {
      state.handedOff = true;
      return log('→ handed back to Recharge');
    }
    // The popup is modal, so the customer can't have clicked a portal button while it's open.
    // Block the event rather than let a second delay run alongside the popup's.
    if (state.open) {
      if (event.cancelable) event.preventDefault();
      return log('→ popup is open, skip event blocked');
    }

    const route = currentRoute();
    const ids = payloadIds(event, route);
    log('click::skip', { route, subscriptionIds: ids, detail: event.detail });

    if (state.failOpen) return log('→ an earlier error turned the popup off for this visit, Recharge flow continues');
    if (!event.cancelable) return log('→ event is not cancelable, Recharge flow continues');
    if (!state.ready) return log('→ data still loading, Recharge flow continues');
    if (!ROUTES.some((re) => re.test(route))) return log('→ page not in scope, Recharge flow continues');

    const entries = ids.map((id) => state.subs.get(id));
    if (!entries.length || !entries.every((x) => x && x.skippable)) {
      return log('→ includes items this script does not handle, Recharge flow continues');
    }
    const targets = entries.filter((x) => x.eligible);
    if (!targets.length) {
      return log('→ no offer, Recharge flow continues:', entries.map((x) => `${x.id}: ${x.why}`).join('; '));
    }

    event.preventDefault(); // must happen synchronously
    const fresh = performance.now() - state.lastTargetAt < 1500;
    openOffer({ route, entries, targets, trigger: fresh ? state.lastTarget : null });
  });

  // Logged in debug mode only, to confirm which event the "Delay your next box" button emits.
  document.addEventListener('Recharge::click::reschedule', (e) => log('click::reschedule (not handled)', e.detail));

  // Keep the cached history current when the portal changes things itself.
  ['Recharge::action::skip', 'Recharge::action::unskip', 'Recharge::action::reschedule', 'Recharge::action::orderChanged'].forEach(
    (name) =>
      document.addEventListener(name, (e) => {
        log(name, e.detail);
        scheduleRefresh();
      })
  );

  function currentRoute() {
    const m = PORTAL_PATH.exec(location.pathname);
    return m ? m[1].replace(/\/+$/, '') : '';
  }

  function payloadIds(event, route) {
    const detail = (event && event.detail) || {};
    const payload = detail.payload || detail;
    const raw = payload.subscriptionIds != null ? payload.subscriptionIds : payload.subscriptionId;
    const ids = [].concat(raw == null ? [] : raw).filter((x) => x !== null && x !== undefined && x !== '').map(String);
    if (ids.length) return ids;
    // Fallbacks: /subscriptions/{id}, ?subscriptionId=, or the customer's only subscription.
    const fromPath = (/^subscriptions\/(\d+)/.exec(route) || [])[1];
    const fromQuery = new URLSearchParams(location.search).get('subscriptionId');
    if (fromPath || fromQuery) return [fromPath || fromQuery];
    return state.subs.size === 1 ? [...state.subs.keys()] : [];
  }

  // ---------------------------------------------------------------------------------------
  // Data: SDK, login, order history, plans
  // ---------------------------------------------------------------------------------------
  const PAST_STATUSES = ['skipped', 'success', 'error', 'refunded', 'partially_refunded'];

  async function boot() {
    try {
      state.rc = await loadSdk();
      state.session = await login();
      await refresh();
    } catch (err) {
      log('setup failed, popup off for this visit (Recharge flow runs as usual):', err);
    }
  }

  function loadSdk() {
    // Reuse an SDK instance that's already on the page; never re-initialise someone else's.
    const ready = () => window.recharge && window.recharge.auth && window.recharge.auth.loginCustomerPortal;
    if (ready()) return Promise.resolve(window.recharge);

    // Another extension may already be loading it: wait for that copy instead of adding a second.
    const pending = document.querySelector('script[src*="/assets/storefront/recharge-client"]');
    if (pending) {
      return new Promise((resolve, reject) => {
        pending.addEventListener('load', () => (ready() ? resolve(window.recharge) : reject(new Error('Recharge SDK missing'))), { once: true });
        pending.addEventListener('error', () => reject(new Error('Recharge SDK failed to load')), { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = CONFIG.sdkUrl;
      s.async = true;
      s.onload = () => {
        const rc = window.recharge;
        if (!rc || !rc.init) return reject(new Error('Recharge SDK loaded without init()'));
        rc.init({
          appName: 'hn-skip-offer',
          appVersion: VERSION,
          loginRetryFn: async () => (state.session = await rc.auth.loginCustomerPortal()),
        });
        resolve(rc);
      };
      s.onerror = () => reject(new Error(`Couldn't load ${CONFIG.sdkUrl}`));
      document.head.appendChild(s);
    });
  }

  async function login() {
    let session;
    try {
      session = await state.rc.auth.loginCustomerPortal(); // uses ?token= and the customer hash in the URL
    } catch (err) {
      log('loginCustomerPortal failed, trying app proxy login', err);
      session = await state.rc.auth.loginShopifyAppProxy();
    }
    if (!session || !session.apiToken) throw new Error('No Recharge session');
    return session;
  }

  async function refresh() {
    const res = await state.rc.subscription.listSubscriptions(state.session, { status: 'active', limit: 250 });
    const entries = await Promise.all(((res && res.subscriptions) || []).map(buildEntry));
    state.subs = new Map(entries.map((x) => [x.id, x]));
    state.ready = true;
    log(
      'ready',
      entries.map((x) => ({
        id: x.id,
        every: COPY.frequency(x.freq, x.unit),
        raw: { order_interval_frequency: x.sub.order_interval_frequency, order_interval_unit: x.sub.order_interval_unit },
        next: x.nextDate,
        previous: x.previous,
        popup: x.eligible,
        why: x.why,
      }))
    );
  }

  function scheduleRefresh() {
    if (!state.session) return;
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(() => refresh().catch((e) => log('refresh failed', e)), 1500);
  }

  // A subscription gets the offer when it's on a shorter cycle than the offer, the order
  // scheduled right before its next one was skipped, and its product has a 2-month plan.
  async function buildEntry(sub) {
    const x = {
      sub,
      id: String(sub.id),
      nextDate: isoDate(sub.next_charge_scheduled_at),
      freq: Number(sub.order_interval_frequency) || 0,
      unit: normUnit(sub.order_interval_unit),
      skippable: false,
      repeat: false,
      eligible: false,
      previous: null,
      planId: null,
      why: '',
    };
    const because = (why) => ((x.why = why), x);

    x.skippable = !sub.is_prepaid && sub.is_skippable !== false && Boolean(x.nextDate);
    if (!x.skippable) return because('prepaid, not skippable or no next order date');
    if (!(x.freq > 0) || !['day', 'week', 'month'].includes(x.unit)) return because('unknown frequency');
    if (!FORCE && (x.unit !== CONFIG.offer.unit || x.freq >= CONFIG.offer.frequency)) {
      return because(`already ${COPY.frequency(x.freq, x.unit)}`);
    }

    try {
      x.previous = await previousCharge(sub, x.nextDate);
    } catch (err) {
      log(`couldn't read the order history of subscription ${x.id}`, err);
      if (!FORCE) return because('order history unavailable');
    }
    x.repeat = Boolean(x.previous && x.previous.status === 'skipped');
    if (!x.repeat && !FORCE) return because('the order before the next one was not skipped');

    const plan = await findPlan(sub);
    if (plan === null && !FORCE) return because(`the product has no ${OFFER_TEXT} plan`);
    x.planId = plan || null; // no plan id: the switch sets the interval fields directly
    x.eligible = true;
    return because(x.repeat ? 'repeat delay' : 'test mode');
  }

  async function previousCharge(sub, nextDate) {
    const base = { purchase_item_id: sub.id, sort_by: 'scheduled_at-desc', limit: 25 };
    const list = async (query) => ((await state.rc.charge.listCharges(state.session, query)) || {}).charges || [];
    let charges;
    try {
      charges = await list({ ...base, status: PAST_STATUSES });
    } catch (err) {
      log('status-filtered charge list failed, retrying without the filter', err);
      charges = await list(base);
    }
    const prev = charges
      .filter((c) => {
        const d = isoDate(c.scheduled_at);
        return d && d < nextDate && String(c.status).toLowerCase() !== 'queued' && includesItem(c, sub.id);
      })
      .sort((a, b) => isoDate(b.scheduled_at).localeCompare(isoDate(a.scheduled_at)) || b.id - a.id)[0];
    return prev ? { id: prev.id, date: isoDate(prev.scheduled_at), status: String(prev.status).toLowerCase() } : null;
  }

  function includesItem(charge, id) {
    const items = charge.line_items;
    if (!items || !items.length) return true; // already filtered by purchase_item_id on the server
    return items.some((li) => String(li.purchase_item_id) === String(id));
  }

  // The product's own plan for the offered frequency, so price and discount rules stay exactly
  // as configured. Resolves to a plan id; null when the product doesn't offer that frequency
  // (for this variant, in the portal); undefined when plans couldn't be read.
  async function findPlan(sub) {
    const productId = sub.external_product_id && sub.external_product_id.ecommerce;
    if (!productId) return undefined;
    const variantId = String((sub.external_variant_id && sub.external_variant_id.ecommerce) || '');
    const key = `${productId}:${variantId}`;
    if (state.plans.has(key)) return state.plans.get(key);
    try {
      const res = await state.rc.plan.listPlans(state.session, { external_product_id: productId, type: 'subscription' });
      const plans = (res && res.plans) || [];
      if (!plans.length) return undefined; // nothing to go on; don't rule the product out
      const match = plans.find((p) => {
        const prefs = p.subscription_preferences || {};
        const every = Number(prefs.order_interval_frequency);
        const charge = prefs.charge_interval_frequency == null ? every : Number(prefs.charge_interval_frequency);
        return (
          !p.deleted_at &&
          normUnit(prefs.interval_unit) === CONFIG.offer.unit &&
          every === CONFIG.offer.frequency &&
          charge === CONFIG.offer.frequency &&
          (!p.has_variant_restrictions || (p.external_variant_ids || []).map(String).includes(variantId)) &&
          !(p.channel_settings && p.channel_settings.customer_portal && p.channel_settings.customer_portal.display === false)
        );
      });
      const result = match ? match.id : null;
      state.plans.set(key, result);
      return result;
    } catch (err) {
      log('plan lookup failed, the switch will set the interval directly', err);
      return undefined;
    }
  }

  // ---------------------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------------------
  const tagged = (cause, flags) => Object.assign(new Error((cause && cause.message) || String(cause)), { cause }, flags);

  // One call at a time, so a failure part-way through is known to be partial and is never
  // offered as a simple retry.
  async function each(items, fn) {
    let done = 0;
    for (const x of items) {
      try {
        await fn(x);
      } catch (cause) {
        throw tagged(cause, { partial: done > 0 });
      }
      done += 1;
    }
  }

  const withTimeout = (promise, ms) =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(tagged(new Error(`No response after ${ms} ms`), { timeout: true })), ms);
      promise.then(
        (v) => (clearTimeout(t), resolve(v)),
        (e) => (clearTimeout(t), reject(e))
      );
    });

  const skipNext = (entries) =>
    each(entries, (x) => state.rc.subscription.skipSubscriptionCharge(state.session, x.sub.id, x.nextDate));

  const switchFrequency = (targets) =>
    each(targets, async (x) => {
      const planId = x.planId || (await findPlan(x.sub));
      const body = planId
        ? { plan_id: planId }
        : {
            order_interval_frequency: CONFIG.offer.frequency,
            charge_interval_frequency: CONFIG.offer.frequency,
            order_interval_unit: CONFIG.offer.unit,
          };
      log(`updating subscription ${x.id}`, body);
      await state.rc.subscription.updateSubscription(state.session, x.sub.id, body, { commit: true });
    });

  function track(name, data = {}) {
    const detail = { event: `hn_skip_offer_${name}`, ...data };
    document.dispatchEvent(new CustomEvent(`HN::skipOffer::${name}`, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
    log('track', detail);
  }

  // ---------------------------------------------------------------------------------------
  // Popup
  // ---------------------------------------------------------------------------------------
  function openOffer(ctx) {
    injectStyles();
    state.open = true;

    const main = ctx.targets[0];
    const vars = { offer: OFFER_TEXT, current: COPY.frequency(main.freq, main.unit) };
    const delayFirst = CONFIG.switchMode === 'delay_then_switch';
    const delayDate = addInterval(main.nextDate, main.freq, main.unit);
    const switchDate = delayFirst ? delayDate : main.nextDate;

    const root = h('div', { id: 'hnso', class: 'hnso-root' });
    const dialog = h('div', {
      class: 'hnso-dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'hnso-title',
      'aria-describedby': 'hnso-body',
      tabindex: '-1',
    });
    root.appendChild(dialog);
    applyPortalTheme(ctx.trigger, root);
    state.root = root;

    let busy = false;
    let changed = false;
    let reloadOnClose = false;
    let buttons = [];
    let switchBtn, delayBtn, alertBox;

    const header = (title) => {
      const closeBtn = h('button', { type: 'button', class: 'hnso-close', 'aria-label': COPY.close });
      closeBtn.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="m1 1 12 12M1 13 13 1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      closeBtn.addEventListener('click', () => close({ reason: 'close_button' }));
      buttons.push(closeBtn);
      return h('div', { class: 'hnso-header' }, [h('h2', { class: 'hnso-title', id: 'hnso-title' }, title), closeBtn]);
    };

    const choice = (kind, label, detail, onClick) => {
      const btn = h('button', { type: 'button', class: `hnso-choice hnso-choice--${kind}` }, [
        h('span', { class: 'hnso-choice-label', 'data-label': label }, label),
        h('span', { class: 'hnso-choice-detail' }, detail),
      ]);
      btn.addEventListener('click', onClick);
      buttons.push(btn);
      return btn;
    };

    function viewOffer() {
      buttons = [];
      dialog.textContent = '';
      alertBox = h('div', { class: 'hnso-alert', role: 'alert', hidden: '' });
      const head = header(fmt(COPY.title, vars));
      if (FORCE) dialog.append(h('p', { class: 'hnso-tag' }, 'Test mode · shown on every delay'));
      switchBtn = choice('primary', fmt(COPY.switchLabel, vars), fmt(COPY.switchDetail, { ...vars, date: fmtDate(switchDate) }), onSwitch);
      delayBtn = choice('secondary', fmt(COPY.delayLabel, vars), fmt(COPY.delayDetail, { ...vars, date: fmtDate(delayDate) }), onDelay);
      dialog.append(head, h('p', { class: 'hnso-text', id: 'hnso-body' }, fmt(COPY.body, vars)), alertBox, h('div', { class: 'hnso-choices' }, [switchBtn, delayBtn]));
    }

    function viewDone(title, body, label = COPY.done) {
      busy = false;
      buttons = [];
      dialog.removeAttribute('aria-busy');
      dialog.textContent = '';
      const done = h('button', { type: 'button', class: 'hnso-btn' }, label);
      done.addEventListener('click', () => close({ reason: 'done' }));
      buttons.push(done);
      dialog.append(header(title), h('p', { class: 'hnso-text', id: 'hnso-body' }, body), done);
      done.focus({ preventScroll: true });
    }

    function setBusy(btn, label) {
      busy = true;
      dialog.setAttribute('aria-busy', 'true');
      alertBox.hidden = true;
      // aria-disabled rather than disabled, so keyboard focus stays on the button
      buttons.forEach((b) => b.setAttribute('aria-disabled', 'true'));
      btn.classList.add('is-busy');
      btn.querySelector('.hnso-choice-label').textContent = label;
    }

    function showError(message, focusBtn) {
      busy = false;
      dialog.removeAttribute('aria-busy');
      buttons.forEach((b) => {
        b.removeAttribute('aria-disabled');
        b.classList.remove('is-busy');
        const l = b.querySelector('.hnso-choice-label');
        if (l) l.textContent = l.dataset.label;
      });
      alertBox.textContent = message;
      alertBox.hidden = false;
      if (focusBtn) focusBtn.focus({ preventScroll: true });
    }

    function failed(err, action) {
      log(`${action} failed`, err);
      state.failOpen = true; // from now on, delays skip the popup: never trap a customer
      track('error', { action, message: String((err && err.message) || err), timeout: Boolean(err && err.timeout), partial: Boolean(err && err.partial) });
      if (err && (err.timeout || err.partial)) {
        // Outcome unknown: no retry. Closing reloads, so the page shows the real schedule.
        changed = true;
        reloadOnClose = true;
        return viewDone(COPY.uncertainTitle, COPY.uncertainBody, COPY.refresh);
      }
      showError(COPY.error, action === 'switch' ? switchBtn : delayBtn);
    }

    async function afterChange() {
      changed = true;
      reloadOnClose = reloadOnClose || CONFIG.reloadAfterChange;
      await withTimeout(refresh(), 8000).catch((e) => log('refresh after change failed', e));
      document.dispatchEvent(new CustomEvent('Affinity:refresh'));
    }

    // The next order date Recharge now has on file. When a delay should have moved it but the
    // fresh data doesn't show that (yet), fall back to our own estimate.
    const newNextDate = (estimate, mustMove) => {
      const fresh = state.subs.get(main.id);
      const d = fresh && fresh.nextDate;
      if (!d || (mustMove && d <= main.nextDate)) return estimate;
      return d;
    };

    async function onSwitch() {
      if (busy) return;
      setBusy(switchBtn, COPY.switching);
      let delayed = false;
      try {
        await withTimeout(
          (async () => {
            if (delayFirst) {
              await skipNext(ctx.entries);
              delayed = true;
            }
            await switchFrequency(ctx.targets);
          })(),
          CONFIG.actionTimeout
        );
      } catch (err) {
        if (!delayed || err.timeout || err.partial) return failed(err, 'switch');
        // The delay went through but the frequency didn't change. Say exactly that; no retry.
        log('switch failed after the delay went through', err);
        state.failOpen = true;
        track('error', { action: 'switch', afterDelay: true, message: String((err && err.message) || err) });
        await afterChange();
        return viewDone(COPY.delayedTitle, fmt(COPY.partialBody, { ...vars, date: fmtDate(newNextDate(delayDate, true)) }));
      }
      await afterChange();
      const date = newNextDate(switchDate, delayFirst);
      track('switched', { subscriptionIds: ctx.targets.map((x) => x.sub.id), mode: CONFIG.switchMode, nextDate: date });
      viewDone(fmt(COPY.switchedTitle, vars), fmt(COPY.switchedBody, { ...vars, date: fmtDate(date) }));
    }

    async function onDelay() {
      if (busy) return;
      if (handBack()) {
        track('delayed', { via: 'recharge', subscriptionIds: ctx.entries.map((x) => x.sub.id) });
        return close({ reason: 'handoff', instant: true, restoreFocus: false });
      }
      setBusy(delayBtn, COPY.delaying);
      try {
        await withTimeout(skipNext(ctx.entries), CONFIG.actionTimeout);
      } catch (err) {
        return failed(err, 'delay');
      }
      await afterChange();
      const date = newNextDate(delayDate, true);
      track('delayed', { via: 'popup', subscriptionIds: ctx.entries.map((x) => x.sub.id), nextDate: date });
      viewDone(COPY.delayedTitle, fmt(COPY.delayedBody, { ...vars, date: fmtDate(date) }));
    }

    // Give the click back to Recharge's own flow, if the button the customer used is still there.
    function handBack() {
      const t = ctx.trigger;
      if (!t || !t.isConnected) return false;
      const target = (t.closest && t.closest('button, a[href], [role="button"], [role="menuitem"]')) || t;
      state.bypass = true;
      state.handedOff = false;
      try {
        target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window }));
      } catch (err) {
        log('hand-off click failed', err);
      } finally {
        state.bypass = false;
      }
      return state.handedOff;
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (!busy) close({ reason: 'escape' });
      } else if (e.key === 'Tab') {
        trapFocus(e, dialog);
      }
    }

    function close({ reason, instant = false, restoreFocus = true }) {
      if (!state.open || busy) return;
      state.open = false;
      state.root = null;
      document.removeEventListener('keydown', onKey, true);
      if (!changed && reason !== 'handoff') track('dismissed', { reason });
      if (reloadOnClose) return location.reload();
      document.documentElement.classList.remove('hnso-lock');
      root.classList.remove('is-open');
      const quick = instant || matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(() => root.remove(), quick ? 0 : 200);
      const back = restoreFocus && focusable(ctx.trigger);
      if (back) back.focus({ preventScroll: true });
    }

    root.addEventListener('click', (e) => {
      if (e.target === root) close({ reason: 'backdrop' });
    });
    document.addEventListener('keydown', onKey, true);

    viewOffer();
    document.body.appendChild(root);
    document.documentElement.classList.add('hnso-lock');
    void root.offsetWidth; // commit the start styles so the fade-in runs
    root.classList.add('is-open');
    dialog.focus({ preventScroll: true });

    track('shown', {
      subscriptionIds: ctx.targets.map((x) => x.sub.id),
      previousCharge: main.previous,
      route: ctx.route,
      testMode: FORCE,
    });
  }

  // ---------------------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------------------
  function h(tag, attrs = {}, children) {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
    [].concat(children == null ? [] : children).forEach((c) => node.append(c));
    return node;
  }

  const fmt = (template, vars) => String(template).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));

  const isoDate = (value) => (value ? String(value).slice(0, 10) : '');

  const normUnit = (unit) => String(unit || '').toLowerCase().replace(/s$/, '');

  // Estimate of the next order date after one delay (the popup replaces it with Recharge's
  // own date once the change is saved).
  function addInterval(iso, n, unit) {
    const [y, m, d] = iso.split('-').map(Number);
    let target;
    if (unit === 'month') {
      target = new Date(y, m - 1 + n, 1);
      target.setDate(Math.min(d, new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()));
    } else {
      target = new Date(y, m - 1, d + n * (unit === 'week' ? 7 : 1));
    }
    const pad = (v) => String(v).padStart(2, '0');
    return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`;
  }

  let dateFormatter;
  function fmtDate(iso) {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!dateFormatter) {
      const opts = { month: 'long', day: 'numeric', year: 'numeric' };
      try {
        dateFormatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, opts);
      } catch (e) {
        dateFormatter = new Intl.DateTimeFormat(undefined, opts);
      }
    }
    return dateFormatter.format(new Date(y, m - 1, d));
  }

  function focusable(node) {
    if (!node || !node.isConnected) return null;
    return node.closest('button, a[href], [role="button"], [role="menuitem"], [tabindex]') || null;
  }

  function trapFocus(e, container) {
    const items = [...container.querySelectorAll('button, a[href]')].filter((n) => n.getClientRects().length > 0);
    if (!items.length) {
      e.preventDefault();
      container.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!container.contains(active) || active === container) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // The popup is mounted on <body>, outside the portal's own markup. Copy the portal's theme
  // tokens from the element the customer clicked so colors, fonts and corners match.
  const THEME_TOKENS = [
    '--recharge-cards-background',
    '--recharge-typography-primary',
    '--recharge-typography-body-font-family',
    '--recharge-typography-body-font-weight',
    '--recharge-typography-heading-font-family',
    '--recharge-typography-heading-font-weight',
    '--recharge-typography-size-4',
    '--recharge-typography-size-5',
    '--recharge-typography-size-6',
    '--recharge-button-brand',
    '--recharge-button-color',
    '--recharge-button-border-radius',
    '--recharge-color-brand-85',
    '--recharge-color-neutral-70',
    '--recharge-corners-radius',
    '--recharge-color-critical',
    '--recharge-color-critical10',
  ];
  function applyPortalTheme(source, target) {
    const ref = (source && source.isConnected && source) || document.querySelector('[class*="recharge"]') || document.body;
    try {
      const cs = getComputedStyle(ref);
      THEME_TOKENS.forEach((name) => {
        const v = cs.getPropertyValue(name).trim();
        if (v) target.style.setProperty(name, v);
      });
      target.style.setProperty('--hnso-ref-font', cs.fontFamily);
    } catch (e) {
      /* fall back to the defaults in the stylesheet */
    }
  }

  // Every rule is scoped under #hnso so the store theme's button/heading styles can't leak in.
  function injectStyles() {
    if (document.getElementById('hnso-styles')) return;
    const style = document.createElement('style');
    style.id = 'hnso-styles';
    style.textContent = `
html.hnso-lock,html.hnso-lock body{overflow:hidden}
#hnso{
  --hnso-bg:var(--recharge-cards-background,#fff);
  --hnso-text:var(--recharge-typography-primary,#141414);
  --hnso-brand:var(--recharge-button-brand,#4d6f61);
  --hnso-on-brand:var(--recharge-button-color,#fff);
  --hnso-soft:var(--recharge-color-brand-85,#e4e9e7);
  --hnso-radius:var(--recharge-corners-radius,8px);
  --hnso-btn-radius:var(--recharge-button-border-radius,8px);
  --hnso-critical:var(--recharge-color-critical,#cc2929);
  --hnso-critical-bg:var(--recharge-color-critical10,#fde8e8);
  position:fixed;inset:0;z-index:2147483000;box-sizing:border-box;margin:0;
  display:flex;align-items:center;justify-content:center;padding:16px;
  background:rgba(11,19,23,.4);
  font-family:var(--recharge-typography-body-font-family,var(--hnso-ref-font,inherit));
  font-weight:var(--recharge-typography-body-font-weight,400);
  font-size:var(--recharge-typography-size-5,16px);line-height:1.5;color:var(--hnso-text);
  text-align:left;letter-spacing:normal;text-transform:none;
  opacity:0;transition:opacity .2s ease;-webkit-font-smoothing:antialiased;
}
#hnso.is-open{opacity:1}
#hnso *,#hnso *::before,#hnso *::after{box-sizing:border-box}
#hnso [hidden]{display:none!important}
#hnso .hnso-dialog{
  position:relative;width:100%;max-width:460px;max-height:calc(100vh - 32px);overflow:auto;margin:0;
  background:var(--hnso-bg);color:var(--hnso-text);border-radius:var(--hnso-radius);
  padding:24px;box-shadow:0 18px 48px rgba(11,19,23,.22);
  transform:translateY(10px);transition:transform .2s ease;outline:none;
}
#hnso.is-open .hnso-dialog{transform:none}
#hnso .hnso-tag{
  display:inline-block;margin:0 0 12px;padding:3px 10px;border-radius:999px;
  background:#fff1c7;color:#5c4400;font-size:12px;font-weight:600;line-height:1.4;
}
#hnso .hnso-header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin:0 0 8px;padding:0}
#hnso .hnso-title{
  margin:0;padding:0;border:0;color:inherit;text-align:left;text-transform:none;letter-spacing:normal;
  font-family:var(--recharge-typography-heading-font-family,inherit);
  font-weight:var(--recharge-typography-heading-font-weight,600);
  font-size:var(--recharge-typography-size-4,20px);line-height:1.3;
}
#hnso .hnso-text{margin:0 0 20px;padding:0;font-size:inherit;line-height:1.5;color:inherit;max-width:60ch}
#hnso .hnso-close,#hnso .hnso-choice,#hnso .hnso-btn{
  -webkit-appearance:none;appearance:none;margin:0;font:inherit;color:inherit;text-transform:none;letter-spacing:normal;
  text-decoration:none;box-shadow:none;cursor:pointer;min-height:0;min-width:0;height:auto;
}
#hnso .hnso-close{
  flex:none;display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;
  margin:-6px -10px 0 0;padding:0;border:0;border-radius:50%;background:transparent;
}
#hnso .hnso-close:hover{background:var(--hnso-soft)}
#hnso .hnso-choices{display:flex;flex-direction:column;gap:8px;margin:0;padding:0}
#hnso .hnso-choice{
  display:flex;flex-direction:column;align-items:flex-start;gap:2px;width:100%;
  padding:14px 16px;border:2px solid transparent;border-radius:var(--hnso-btn-radius);
  text-align:left;line-height:1.4;transition:filter .15s ease,opacity .15s ease;
}
#hnso .hnso-choice--primary,#hnso .hnso-btn{background:var(--hnso-brand);color:var(--hnso-on-brand)}
#hnso .hnso-choice--secondary{background:var(--hnso-soft);color:var(--hnso-brand)}
#hnso .hnso-choice:hover:not([aria-disabled="true"]),#hnso .hnso-btn:hover{filter:brightness(.93)}
#hnso .hnso-choice-label{display:block;font-weight:600;font-size:inherit}
#hnso .hnso-choice-detail{display:block;font-size:var(--recharge-typography-size-6,14px)}
#hnso [aria-disabled="true"]{cursor:default}
#hnso .hnso-choice[aria-disabled="true"]:not(.is-busy){opacity:.55}
#hnso .hnso-choice.is-busy{cursor:progress}
#hnso .hnso-btn{
  display:block;width:100%;padding:14px 18px;border:2px solid transparent;border-radius:var(--hnso-btn-radius);
  font-weight:600;text-align:center;line-height:1.2;
}
#hnso .hnso-close:focus-visible,#hnso .hnso-choice:focus-visible,#hnso .hnso-btn:focus-visible{
  outline:2px solid var(--hnso-brand);outline-offset:2px;
}
#hnso .hnso-alert{
  margin:0 0 16px;padding:12px;border:1px solid var(--hnso-critical);border-radius:var(--hnso-radius);
  background:var(--hnso-critical-bg);color:#0b1317;font-size:var(--recharge-typography-size-6,14px);line-height:1.45;
}
@media (max-width:599px){
  #hnso{align-items:flex-end;padding:0}
  #hnso .hnso-dialog{
    max-width:none;max-height:92vh;border-radius:var(--hnso-radius) var(--hnso-radius) 0 0;
    padding:20px 20px calc(20px + env(safe-area-inset-bottom,0px));transform:translateY(24px);
  }
}
@media (prefers-reduced-motion:reduce){#hnso,#hnso .hnso-dialog{transition:none}}
`;
    document.head.appendChild(style);
  }

  boot();
})();